/**
 * What a module is, in four chips.
 *
 * The same row is drawn by the card in a feed and by the module's own page, so it
 * is written once — the first build of this feature had two nearly identical chip
 * rows that had already drifted in padding and in whether *18 coins to win* said
 * "to win" at all.
 *
 * The order is the order a reader needs it in: **how much work** (a playlist of
 * six), then **what it is** (Arabic, 1st year), then **what it pays** (18 coins).
 * A chip the record did not state is left out rather than printed empty; a module
 * written before a field existed simply has fewer chips.
 */
import {View} from 'react-native'
import {Plural, Trans, useLingui} from '@lingui/react/macro'

import {gradeLabel, subjectLabel} from '#/lib/curriculum'
import {atoms as a} from '#/alf'
import {Chip} from '#/components/Chip'

export function ModuleFactsRow({
  capsuleCount,
  level,
  subject,
  coins,
  testID,
}: {
  /** How many capsules the playlist holds, when anything has said. */
  capsuleCount: number | null
  /** The grade code, `Level_A5`, when the record states one. */
  level: string | null
  /** The subject code, `Maths`, when the record states one. */
  subject: string | null
  /** Every exercise in every capsule. */
  coins: number | null
  testID?: string
}) {
  const {t: l} = useLingui()

  const levelName = gradeLabel(level)
  const subjectName = subjectLabel(subject)

  return (
    <View
      testID={testID}
      style={[a.flex_row, a.align_center, a.flex_wrap, a.gap_xs]}>
      <Chip
        label={
          capsuleCount != null ? (
            <Plural
              value={capsuleCount}
              one="Playlist · # capsule"
              other="Playlist · # capsules"
            />
          ) : (
            <Trans>Playlist</Trans>
          )
        }
        accent
      />
      {subjectName ? <Chip label={l(subjectName)} /> : null}
      {levelName ? <Chip label={l(levelName)} /> : null}
      {coins != null && coins > 0 ? (
        <Chip
          label={
            <Plural value={coins} one="# coin to win" other="# coins to win" />
          }
        />
      ) : null}
    </View>
  )
}

/**
 * One tag — see `#/components/Chip` for what it is and why it lives there.
 *
 * Re-exported under this name because this is where the module surfaces have always
 * imported it from, and a chip row in a feed, on a module's page and under a
 * classroom in a profile's tab is one vocabulary rather than three.
 */
export {Chip}
